// src/services/address/postal-pt-external/postalPtService.js
// *******************************************************************************
// Portugal Postal Code (Codigo Postal) Service
// *******************************************************************************
// Mirrors addressService.getBrazilCep but for PT via the GeoAPI.pt factory.
// Returns the same { case, data, error } shape so SimpleFormProvider's
// handleAddressZipBlur can consume BR and PT results identically.
// *******************************************************************************

import translateX from 'utils/translateX';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';

// Converts the universal POJO from the provider adapter into the application
// addressSchema shape (countryCode PT). Mirrors addressService.decorateAddressFromPojo.
const decoratePtAddressFromPojo = pojo => {
  if (!pojo) return null;

  const { source, street, neighborhood, city, region, coordinates, hasMultipleAddresses } = pojo;
  // Fresh provider POJO uses `zip`; the cached DB doc uses `cp`.
  const zip = pojo.zip || pojo.cp;

  return {
    zip: zip || '',
    addressTitle: translateX('main_address'),

    // Universal POJO -> addressSchema (PT label mapping)
    address1: street || '', // Morada
    address2: '',
    neighborhood: neighborhood || '', // Localidade
    city: city || '', // Concelho
    region: region || '', // Distrito (full name, not a 2-letter UF)

    // Virtual display field
    __city: city && region ? `${city} - ${region}` : city || '',

    // Coordinates from GeoAPI (skip extra GPS call when present)
    ...(Array.isArray(coordinates) && coordinates.length === 2
      ? { addressCoordinates: coordinates }
      : {}),

    addressAdditionalInfo: {
      searchApi: source || '',
    },

    addressMetadata: {
      countryCode: 'PT',
      foundByApi: true,
      apiUrl: '',
      zipWithMultipleAddress: hasMultipleAddresses || false,
      manual: false,
      apiFailure: false,
    },
  };
};

// Main entry: look up a Portuguese postal code (7 digits) via the backend proxy
// (/v1/postal-pt-search/search) and return the decorated address. The backend
// holds the GeoAPI key + a long-lived DB cache, so repeat lookups never hit the
// external rate limit. Result shape matches addressService.getBrazilCep:
//   { case: 1, data: null, error }   -> API failure (provider/backend down)
//   { case: 2, data: null, error }   -> postal code not found
//   { case: 3, data: {...}, error: null } -> success
const getPortugalPostalCode = async cp => {
  const cleanCp = String(cp || '').replace(/\D/g, '');

  if (cleanCp.length !== 7) {
    return { case: 2, data: null, error: 'Invalid postal code length' };
  }

  try {
    // axiosService.post({ returnAsObject: true }) resolves to [response, error]
    const response = await axiosService.post({
      url: API.POSTAL_PT_SEARCH.SEARCH,
      data: { cp: cleanCp },
      returnAsObject: true,
    });
    const actualResponse = Array.isArray(response) ? response[0] : response;
    const pojo = actualResponse?.data;

    if (!actualResponse?.success || !pojo?.success) {
      // apiWorked === false means the provider/backend is down -> API failure
      const apiDown = pojo ? pojo.apiWorked === false : true;

      if (apiDown) {
        appService.notification({
          key: 'cepApiErrorNotificationKey',
          flag: 'e',
          title: translateX('cep_service_unavailable'),
          description: translateX('cep_service_unavailable_description'),
          duration: 0,
        });
        return { case: 1, data: null, error: pojo?.error || 'Postal service unavailable' };
      }

      appService.notification({
        flag: 'w',
        title: translateX('zip_not_found'),
        description: translateX('cep_not_found_description'),
        duration: 10,
      });
      return { case: 2, data: null, error: pojo?.error || 'Postal code not found' };
    }

    const decoratedAddress = decoratePtAddressFromPojo(pojo);

    return { case: 3, data: decoratedAddress, error: null };
  } catch (error) {
    appService.notification({
      key: 'cepApiErrorNotificationKey',
      flag: 'e',
      title: translateX('cep_service_unavailable'),
      description: translateX('cep_service_unavailable_description'),
      duration: 0,
    });

    return { case: 1, data: null, error: error.message || 'Postal service unavailable' };
  }
};

export { getPortugalPostalCode, decoratePtAddressFromPojo };

export default { getPortugalPostalCode, decoratePtAddressFromPojo };
